import React, { useState, useMemo, useEffect } from 'react';
import { Document, Page, Text, View, StyleSheet, Font, pdf } from '@react-pdf/renderer';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { CalendarIcon, DocumentArrowDownIcon, UserIcon } from '@heroicons/react/24/outline';
import { db } from '../../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { downloadPDF } from '../../utils/pdfUtils';

// Register font like in OrderReceipt
Font.register({
  family: 'Roboto',
  src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf'
});

// Reuse most of the styles from OrderReceipt
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Roboto',
    fontSize: 12,
  },
  headerContainer: {
    marginBottom: 20,
    borderBottom: 2,
    borderBottomColor: '#333',
    paddingBottom: 10,
  },
  businessHeader: {
    fontSize: 20,
    textAlign: 'center',
    marginBottom: 8,
    color: '#1a365d',
    fontWeight: 'bold',
  },
  businessInfo: {
    fontSize: 10,
    textAlign: 'center',
    color: '#4a5568',
    marginBottom: 4,
  },
  receiptTitle: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#2563eb',
  },
  summarySection: {
    marginBottom: 30,
    padding: 15,
    backgroundColor: '#f7fafc',
    borderRadius: 4,
    border: 1,
    borderColor: '#e2e8f0',
  },
  summaryTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#1f2937',
    borderBottom: 1,
    borderBottomColor: '#e2e8f0',
    paddingBottom: 5,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  summaryLabel: {
    color: '#4b5563',
  },
  summaryValue: {
    fontWeight: 'bold',
    color: '#1f2937',
  },
  ordersSection: {
    marginTop: 20,
  },
  ordersSectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#1f2937',
  },
  table: {
    display: 'table',
    width: 'auto',
    marginVertical: 10,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  tableHeader: {
    backgroundColor: '#f3f4f6',
  },
  tableHeaderCell: {
    padding: 8,
    fontSize: 10,
    fontWeight: 'bold',
    color: '#374151',
    borderRightWidth: 1,
    borderRightColor: '#e5e7eb',
    textAlign: 'center',
  },
  tableCell: {
    padding: 8,
    fontSize: 10,
    borderRightWidth: 1,
    borderRightColor: '#e5e7eb',
    textAlign: 'center',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    fontSize: 10,
    color: '#6b7280',
  },
  orderBlock: {
    marginBottom: 20,
    border: 1,
    borderColor: '#e5e7eb',
    borderRadius: 4,
  },
  orderHeader: {
    flexDirection: 'row',
    backgroundColor: '#f8fafc',
    padding: 8,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  orderHeaderCell: {
    flex: 1,
    fontSize: 10,
    color: '#374151',
    fontWeight: 'bold',
  },
  orderRow: {
    flexDirection: 'row',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    backgroundColor: '#ffffff',
  },
  orderCell: {
    flex: 1,
    fontSize: 10,
  },
  itemsTable: {
    marginTop: 8,
    backgroundColor: '#f3f4f6',
    borderRadius: 4,
    overflow: 'hidden',
  },
  itemsHeader: {
    flexDirection: 'row',
    backgroundColor: '#e5e7eb',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#d1d5db',
  },
  itemHeaderCell: {
    flex: 1,
    fontSize: 10,
    fontWeight: 'bold',
    color: '#374151',
  },
  orderSection: {
    marginBottom: 15,
    padding: 10,
    backgroundColor: '#ffffff',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
});

const SalesReport = ({ orders, dateRange, businessDetails, customerFilter, cratesData }) => {
  const filteredOrders = useMemo(() => {
    if (!orders || !Array.isArray(orders)) return [];
    
    let filtered = orders.filter(order => 
      order && 
      order.status === 'completed' && 
      order.customerName &&
      order.totalAmount &&
      order.amountReceived !== undefined &&
      order.remainingBalance !== undefined
    );
    
    if (dateRange?.startDate && dateRange?.endDate) {
      filtered = filtered.filter(order => {
        const orderDate = order.finalizedAt ? new Date(order.finalizedAt) 
          : order.date?.seconds ? new Date(order.date.seconds * 1000)
          : new Date(order.date);
          
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        orderDate.setHours(0, 0, 0, 0);

        return orderDate >= startDate && orderDate <= endDate;
      });
    }
    
    if (customerFilter) {
      filtered = filtered.filter(order => 
        order.customerName === customerFilter
      );
    }
    
    return filtered.sort((a, b) => {
      const dateA = a.finalizedAt ? new Date(a.finalizedAt) 
        : a.date?.seconds ? new Date(a.date.seconds * 1000)
        : new Date(a.date);
      const dateB = b.finalizedAt ? new Date(b.finalizedAt)
        : b.date?.seconds ? new Date(b.date.seconds * 1000)
        : new Date(b.date);
      return dateA - dateB;
    });
  }, [orders, dateRange, customerFilter]);

  // Early return if required props are missing
  if (!orders?.length || !dateRange || !businessDetails) {
    return null;
  }

  // If no orders found after filtering, show a message
  if (!filteredOrders.length) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <View style={styles.headerContainer}>
            <Text style={styles.businessHeader}>{businessDetails?.name}</Text>
            <Text style={styles.businessInfo}>{businessDetails?.address}</Text>
            <Text style={styles.businessInfo}>
              Phone: {businessDetails?.phone} | Email: {businessDetails?.email}
            </Text>
            <Text style={styles.businessInfo}>GST: {businessDetails?.gstNumber}</Text>
          </View>

          <Text style={styles.receiptTitle}>
            {customerFilter ? `Sales Report - ${customerFilter}` : 'Sales Report'}
          </Text>

          <View style={styles.summarySection}>
            <Text style={styles.summaryTitle}>Report Summary</Text>
            <Text style={styles.summaryRow}>
              Period: {dateRange.startDate?.toLocaleDateString()} to {dateRange.endDate?.toLocaleDateString()}
            </Text>
          </View>

          <View style={[styles.summarySection, { alignItems: 'center', justifyContent: 'center' }]}>
            <Text style={{ color: '#64748b', fontSize: 14 }}>
              No sales found for the selected date range
            </Text>
          </View>

          <Text style={styles.footer}>
            Generated on {new Date().toLocaleString()} | {businessDetails?.name}
          </Text>
        </Page>
      </Document>
    );
  }

  const totalSales = filteredOrders.reduce((sum, order) => sum + (Number(order.totalAmount) || 0), 0);
  const totalReceived = filteredOrders.reduce((sum, order) => sum + (Number(order.amountReceived) || 0), 0);
  const totalPending = filteredOrders.reduce((sum, order) => sum + (Number(order.remainingBalance) || 0), 0);

  // Define column flexes based on whether a customer is filtered
  const columnFlex = customerFilter ? {
    date: 1.5,
    orderId: 2,
    total: 1.5,
    received: 1.5,
    balance: 1.5
  } : {
    date: 1,
    orderId: 1.5,
    customer: 2,
    total: 1,
    received: 1,
    balance: 1
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header Section */}
        <View style={styles.headerContainer}>
          <Text style={styles.businessHeader}>{businessDetails?.name}</Text>
          <Text style={styles.businessInfo}>{businessDetails?.address}</Text>
          <Text style={styles.businessInfo}>
            Phone: {businessDetails?.phone} | Email: {businessDetails?.email}
          </Text>
          <Text style={styles.businessInfo}>GST: {businessDetails?.gstNumber}</Text>
        </View>

        {/* Report Title */}
        <Text style={styles.receiptTitle}>
          {customerFilter ? `Sales Report - ${customerFilter}` : 'Sales Report'}
        </Text>

        {/* Summary Section */}
        <View style={styles.summarySection}>
          <Text style={styles.summaryTitle}>Report Summary</Text>
          <Text style={styles.summaryRow}>
            Period: {dateRange.startDate?.toLocaleDateString()} to {dateRange.endDate?.toLocaleDateString()}
          </Text>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Orders:</Text>
            <Text style={styles.summaryValue}>{filteredOrders.length}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Sales:</Text>
            <Text style={styles.summaryValue}>Rs. {totalSales.toLocaleString()}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Amount Received:</Text>
            <Text style={styles.summaryValue}>Rs. {totalReceived.toLocaleString()}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Pending Amount:</Text>
            <Text style={styles.summaryValue}>Rs. {totalPending.toLocaleString()}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Boxes With Customer:</Text>
            <Text style={styles.summaryValue}>
              {customerFilter && cratesData[customerFilter] 
                ? cratesData[customerFilter] 
                : Object.values(cratesData).reduce((sum, count) => sum + count, 0)}
            </Text>
          </View>
        </View>

        {/* Orders Section */}
        <View style={styles.ordersSection}>
          <Text style={styles.ordersSectionTitle}>Order Details</Text>
          {filteredOrders.map((order, index) => (
            <View key={order.id} style={styles.orderSection}>
              {/* Order Details Header */}
              <View style={styles.orderHeader}>
                <Text style={[styles.orderHeaderCell, { flex: columnFlex.date }]}>Date</Text>
                <Text style={[styles.orderHeaderCell, { flex: columnFlex.orderId }]}>Order ID</Text>
                {!customerFilter && (
                  <Text style={[styles.orderHeaderCell, { flex: columnFlex.customer }]}>Customer</Text>
                )}
                <Text style={[styles.orderHeaderCell, { flex: columnFlex.total }]}>Total</Text>
                <Text style={[styles.orderHeaderCell, { flex: columnFlex.received }]}>Received</Text>
                <Text style={[styles.orderHeaderCell, { flex: columnFlex.balance }]}>Balance</Text>
              </View>

              {/* Order Details Row */}
              <View style={styles.orderRow}>
                <Text style={[styles.orderCell, { flex: columnFlex.date }]}>
                  {new Date(order.finalizedAt).toLocaleDateString()}
                </Text>
                <Text style={[styles.orderCell, { flex: columnFlex.orderId }]}>{order.id}</Text>
                {!customerFilter && (
                  <Text style={[styles.orderCell, { flex: columnFlex.customer }]}>{order.customerName}</Text>
                )}
                <Text style={[styles.orderCell, { flex: columnFlex.total }]}>Rs. {order.totalAmount?.toLocaleString()}</Text>
                <Text style={[styles.orderCell, { flex: columnFlex.received }]}>Rs. {order.amountReceived?.toLocaleString()}</Text>
                <Text style={[styles.orderCell, { flex: columnFlex.balance }]}>Rs. {(order.totalAmount - order.amountReceived)?.toLocaleString()}</Text>
              </View>

              {/* Items Table */}
              <View style={styles.itemsTable}>
                <View style={styles.itemsHeader}>
                  <Text style={[styles.itemHeaderCell, { flex: 2 }]}>Item</Text>
                  <Text style={[styles.itemHeaderCell, { flex: 1 }]}>Quantity</Text>
                  <Text style={[styles.itemHeaderCell, { flex: 1 }]}>Price/kg</Text>
                  <Text style={[styles.itemHeaderCell, { flex: 1 }]}>Amount</Text>
                </View>

                {order.items?.map((item, itemIndex) => (
                  <View key={itemIndex} style={styles.orderRow}>
                    <Text style={[styles.orderCell, { flex: 2 }]}>{item.vegetable}</Text>
                    <Text style={[styles.orderCell, { flex: 1 }]}>
                      {item.quantity || item.actualQuantity || item.estimatedQuantity} kg
                    </Text>
                    <Text style={[styles.orderCell, { flex: 1 }]}>
                      Rs. {item.pricePerKg || item.unitPrice}
                    </Text>
                    <Text style={[styles.orderCell, { flex: 1 }]}>
                      Rs. {((item.quantity || item.actualQuantity || item.estimatedQuantity) * 
                            (item.pricePerKg || item.unitPrice)).toLocaleString()}
                    </Text>
                  </View>
                ))}
              </View>

              {index < filteredOrders.length - 1 && <View style={{ marginBottom: 20 }} />}
            </View>
          ))}
        </View>

        {/* Footer */}
        <Text style={styles.footer}>
          Generated on {new Date().toLocaleString()} | {businessDetails?.name}
        </Text>
      </Page>
    </Document>
  );
};

// Move DatePickerInput outside and make it a regular component
const DatePickerInput = ({ value, onClick }) => (
  <button
    onClick={onClick}
    type="button"
    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
  >
    <CalendarIcon className="h-5 w-5 mr-2 text-gray-400" aria-hidden="true" />
    {value || 'Select date'}
  </button>
);

// Export PDFDownloadButton
export const PDFDownloadButton = ({ orders, dateRange, businessDetails, customerFilter }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [cratesData, setCratesData] = useState({});

  useEffect(() => {
    const fetchCratesData = async () => {
      try {
        const contactsRef = collection(db, 'contacts');
        let q = query(contactsRef, where('contactType', '==', 'customer'));
        
        if (customerFilter) {
          q = query(q, where('name', '==', customerFilter));
        }
        
        const querySnapshot = await getDocs(q);
        const cratesInfo = {};
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.cratesOut) {
            cratesInfo[data.name] = data.cratesOut;
          }
        });
        
        setCratesData(cratesInfo);
      } catch (error) {
        console.error('Error fetching crates data:', error);
      }
    };

    fetchCratesData();
  }, [customerFilter]);

  if (!orders || !dateRange.startDate || !dateRange.endDate || !businessDetails) {
    return null;
  }

  const handleDownload = async () => {
    setIsGenerating(true);
    try {
      const doc = (
        <SalesReport 
          orders={orders}
          dateRange={dateRange}
          businessDetails={businessDetails}
          customerFilter={customerFilter}
          cratesData={cratesData}
        />
      );
      await downloadPDF(doc, `sales-report-${dateRange.startDate.toISOString().split('T')[0]}-to-${dateRange.endDate.toISOString().split('T')[0]}${customerFilter ? `-${customerFilter}` : ''}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <button
      onClick={handleDownload}
      disabled={isGenerating}
      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400"
    >
      <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
      {isGenerating ? 'Generating Report...' : 'Download Report'}
    </button>
  );
};

export default function SalesReports({ orders }) {
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [businessDetails, setBusinessDetails] = useState(null);
  
  useEffect(() => {
    const fetchBusinessDetails = async () => {
      const docRef = doc(db, 'settings', 'business');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setBusinessDetails(docSnap.data());
      }
    };
    fetchBusinessDetails();
  }, []);

  const filteredOrders = useMemo(() => {
    if (!orders || !Array.isArray(orders)) return [];
    
    let filtered = orders.filter(order => 
      order && 
      order.status === 'completed' && 
      order.customerName &&
      order.totalAmount &&
      order.amountReceived !== undefined &&
      order.remainingBalance !== undefined
    );
    
    if (dateRange.startDate && dateRange.endDate) {
      filtered = filtered.filter(order => {
        if (!order.finalizedAt) return false;
        const orderDate = new Date(order.finalizedAt);
        return orderDate >= dateRange.startDate && orderDate <= dateRange.endDate;
      });
    }
    
    if (selectedCustomer) {
      filtered = filtered.filter(order => 
        order.customerName === selectedCustomer
      );
    }
    
    return filtered;
  }, [orders, dateRange, selectedCustomer]);

  const uniqueCustomers = useMemo(() => {
    if (!orders || !Array.isArray(orders)) return [];
    return [...new Set(orders
      .filter(order => order && order.customerName)
      .map(order => order.customerName))]
      .filter(Boolean)
      .sort();
  }, [orders]);

  const handleCustomerChange = (e) => {
    const value = e.target.value;
    setSelectedCustomer(value);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-[#2E3B4E] shadow rounded-lg p-6">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <DatePicker
              selectsRange={true}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(update) => setDateRange({ startDate: update[0], endDate: update[1] })}
              customInput={<DatePickerInput />}
              isClearable
              dateFormat="MM/dd/yyyy"
            />
            
            <select
              value={selectedCustomer}
              onChange={handleCustomerChange}
              className="rounded-md border border-gray-300 px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
            >
              <option value="">All Customers</option>
              {uniqueCustomers.map(customer => (
                <option key={customer} value={customer}>{customer}</option>
              ))}
            </select>

            {filteredOrders.length > 0 && (
              <PDFDownloadButton
                orders={filteredOrders}
                dateRange={dateRange}
                businessDetails={businessDetails}
                customerFilter={selectedCustomer}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}